import React, { useState, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import RouteTile from "./components/RouteTile";
import RouteMapView from "./components/RouteMapView";
import Card from "components/card";
import RouteDonutChart from "./components/RouteDonutChart";
import RouteLineGraph from "./components/RouteLineGraph";
import KeyPlot from "./components/KeyPlot";
import DeviceDetailsTable from "./components/DeviceDetailsTable";
import NoData from "../../../assets/img/dashboards/9404370.webp";
import playIcon from "../../../assets/img/dashboards/play.png";
import pauseIcon from "../../../assets/img/dashboards/pause.png";
import resetIcon from "../../../assets/img/dashboards/reset.png";
import RoutePieChart from "./components/RoutePieChart";
import RouteSummary from "./components/RouteSummary";
import RouteGauges from "./components/RouteGauges";
import EventFilterDropdown from "./components/EventDropdown";
import DeviceDetails from "./components/DeviceDetails";
import "./route.css";
import StatusBanner from "./components/StatusBanner";
import { readToken } from "utils/session";
import DeviceSelect from "components/DeviceSelect";

const RouteTracking = () => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  
  // Tab State
  const [activeTab, setActiveTab] = useState("static"); // 'static' or 'playback'

  const [cardSliderInfo, setCardSliderInfo] = useState([]);
  const [pieInfo, setPieInfo] = useState([]);
  const [gaugeInfo, setGaugeInfo] = useState([]);
  const [donutInfo, setDonutInfo] = useState([]);
  const [lineGraphInfo, setLineGraphInfo] = useState([]);
  const [tempInfo, setTempInfo] = useState([]);
  const [tempInfoDisplayNames, setTempInfoDisplayNames] = useState([]);
  const [tempInfoName, setTempInfoName] = useState("");
  const [utilInfo, setUtilInfo] = useState([]);
  const [utilInfoDisplayNames, setUtilInfoDisplayNames] = useState([]);
  const [utilInfoName, setUtilInfoName] = useState("");
  const [repDevInfo, setRepDevInfo] = useState([]);
  const [repDevInfoDisplayNames, setRepDevInfoDisplayNames] = useState([]);
  const [repDevInfoName, setRepDevInfoName] = useState("");
  const [mapInfo, setMapInfo] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000); 
  const [currentIndex, setCurrentIndex] = useState(0);
  const [latestGaugeValues, setLatestGaugeValues] = useState({});
  const [eventList, setEventList] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [overlayImg, setOverlayImg] = useState("");
  const [ignitionImg, setIgnitionImg] = useState("");
  const [haltImg, setHaltImg] = useState("");
  const [overSeedImg, setOverSeedImg] = useState("");
  const [deviceDetails, setDeviceDetails] = useState({});

  const USERKEY = readToken();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        let config = {
          method: "get",
          url: `${apiBaseUrl}/api/fleetlist`,
          headers: { Authorization: `Bearer ${USERKEY}` },
        };
        const response = await axios.request(config);
        const fleetData = response.data.fleetlist;
        const devicesData = fleetData.map((device) => ({
          aid: device.aid,
          did: device.did,
        }));
        setDevices(devicesData);
      } catch (error) {
        console.log(error);
      }
    };
    fetchDevices();
  }, [USERKEY, apiBaseUrl]);

  const handleViewReport = async () => {
    if (selectedOptions.length === 0) {
      setPopupMessage("Please select device ID");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }
    if (!fromDate && !toDate) {
      setPopupMessage("Date is required");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      return;
    }

    const reportData = {
      repFrom: format(fromDate, "yyyy-MM-dd"),
      repTo: format(toDate, "yyyy-MM-dd"),
      repDid: selectedOptions,
    };

    let config = {
      method: "get",
      url: `${apiBaseUrl}/api/fetchroute?reportData=${encodeURIComponent(JSON.stringify(reportData))}`,
      headers: { Authorization: `Bearer ${USERKEY}` },
    };

    try {
      const response = await axios.request(config);
      const routeData = response.data.routeData || [];
      
      setEventList(response.data.eventlist || []);
      setMapInfo(routeData);

      // Reset states for fresh data
      setActiveTab("static");
      setIsPlaying(false);
      setCurrentIndex(routeData.length > 0 ? routeData.length - 1 : 0);

      const latestValues = {};
      if (routeData.length > 0 && response.data.playbackData?.gauges) {
        const latestData = routeData[routeData.length - 1]; 
        response.data.playbackData.gauges.forEach((gauge) => {
          latestValues[gauge.tableval] = latestData[gauge.tableval];
        });
      }
      
      setGaugeInfo(response.data.playbackData?.gauges || []);
      setLatestGaugeValues(latestValues);
      setOverlayImg(response.data.deviceDetails?.AssetMapImg || "");
      setIgnitionImg(response.data?.eventlist?.[0]?.eventimg || "");
      setHaltImg(response.data?.eventlist?.[1]?.eventimg || "");
      setOverSeedImg(response.data?.eventlist?.[2]?.eventimg || "");

      const plotData = response.data.plotData || {};
      setCardSliderInfo(plotData.cardSliderInfo?.cardSliderData || []);
      setPieInfo(plotData.PieChartInfo?.PieCharData || []);
      setDonutInfo(plotData.DonutChartInfo?.DonutChartData || []);

      const lineChartData = plotData.LineChartInfo?.LineChartData || [];
      const chartConfigs = lineChartData.map((chartData) => {
        return {
          plotName: chartData.plotData.plotName,
          plottype: chartData.plottype,
          lineGraphInfo: {
            timestamps: routeData.map((item) => item.timestamp),
            datasets: chartData.plotData.yaxisData.map((yKey) => ({
              label: chartData.plotData.displayname[yKey],
              data: routeData.map((route) => route[yKey]),
            })),
          },
        };
      });
      setLineGraphInfo(chartConfigs);

      const tableData = plotData.tableInfo?.tableData || [];
      if(tableData.length > 0) {
        setTempInfo(tableData[0]?.contents || []);
        setTempInfoDisplayNames(tableData[0]?.columns || []);
        setTempInfoName(tableData[0]?.tableName || "");
        setUtilInfo(tableData[1]?.contents || []);
        setUtilInfoDisplayNames(tableData[1]?.columns || []);
        setUtilInfoName(tableData[1]?.tableName || "");
        setRepDevInfo(tableData[2]?.contents || []);
        setRepDevInfoDisplayNames(tableData[2]?.columns || []);
        setRepDevInfoName(tableData[2]?.tableName || "");
      }

      setDeviceDetails(response.data.deviceDetails || {});
      setShowReport(true);
      setShowDownload(true);
    } catch (error) {
      setShowReport(false);
      setShowDownload(false);
      setPopupMessage("No data available for the selected Input");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  const handleDownload = async () => {
    try {
      const element = document.getElementById("reportContent");
      if (!element) return;
      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("RouteTracking.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    if (tab === "static") {
      setIsPlaying(false);
      setCurrentIndex(mapInfo.length > 0 ? mapInfo.length - 1 : 0); // Show full path
    } else {
      setIsPlaying(false);
      setCurrentIndex(0); // Reset to start for playback
    }
  };

  const togglePlayPause = () => setIsPlaying((prev) => !prev);
  const handleSpeedChange = (e) => setSpeed(5000 - Number(e.target.value) + 500);
  const resetPlayback = () => {
    setIsPlaying(false);
    setCurrentIndex(0);
  };

  return (
    <>
      <Card extra="flex flex-col md:flex-row flex-grow items-center rounded-[6px] relative p-5 mt-5 justify-between">
        {/* Dropdown */}
        <DeviceSelect devices={devices} value={selectedOptions} onChange={setSelectedOptions} />

        {/* Datepickers */}
        <div className="flex flex-col items-center space-y-4 md:flex-row md:space-x-3 md:space-y-0">
          <div className="flex w-full space-x-2 md:w-auto">
            <DatePicker
              selected={fromDate}
              onChange={(date) => setFromDate(date)}
              className="z-10 w-full rounded-md bg-lightPrimary p-2 font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-900 md:w-60"
              placeholderText="From Date"
              dateFormat="yyyy/MM/dd"
            />
            <DatePicker
              selected={toDate}
              onChange={(date) => setToDate(date)}
              className="w-full rounded-md bg-lightPrimary p-2 font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-900 md:w-60"
              placeholderText="To Date"
              dateFormat="yyyy/MM/dd"
            />
          </div>
          <button type="button" onClick={handleViewReport} className="ml-3 w-full rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 md:w-auto">
            View Route
          </button>
          {showDownload && (
            <button type="button" onClick={handleDownload} className="ml-3 rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600">
              Download
            </button>
          )}
        </div>
      </Card>

      {showPopup && (
        <div className="mt-5 flex justify-end">
          <div className="rounded-md bg-red-500 p-4 text-white">{popupMessage}</div>
        </div>
      )}

      {showReport ? (
        <div id="reportContent" className="mt-5">
          
          {/* TAB BUTTONS */}
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => handleTabSwitch("static")}
              className={`px-6 py-2 font-bold rounded-lg transition-colors ${activeTab === "static" ? "bg-blue-500 text-white shadow-md" : "bg-white text-gray-600 hover:bg-gray-100 dark:bg-navy-800 dark:text-gray-300"}`}
            >
              Static Route
            </button>
            <button
              onClick={() => handleTabSwitch("playback")}
              className={`px-6 py-2 font-bold rounded-lg transition-colors ${activeTab === "playback" ? "bg-blue-500 text-white shadow-md" : "bg-white text-gray-600 hover:bg-gray-100 dark:bg-navy-800 dark:text-gray-300"}`}
            >
              Playback Studio
            </button>
          </div>

          <div className="grid h-full grid-cols-1 gap-5 md:grid-cols-1">
            
            {/* PLAYBACK CONTROLS (Only visible in Playback Tab) */}
            {activeTab === "playback" && (
              <Card extra="rounded-[20px] p-3 relative">
                <div className="flex flex-wrap justify-between">
                  <div className="flex w-full flex-wrap md:w-auto md:flex-nowrap">
                    <div className="flex w-full flex-row justify-between px-3 pt-2 md:w-auto">
                      <h4 className="mb-3 text-lg font-bold text-navy-700 dark:text-white md:text-xl">Playback Controls</h4>
                    </div>
                    <div className="controls flex w-full flex-wrap md:w-auto">
                      <div className="mb-3 flex items-center">
                        <button onClick={togglePlayPause} className="mr-4">
                          <img src={isPlaying ? pauseIcon : playIcon} alt={isPlaying ? "Pause" : "Play"} className="h-8 w-8" />
                        </button>
                        <button onClick={resetPlayback} className="mr-4">
                          <img src={resetIcon} alt="Reset" className="h-8 w-8" />
                        </button>
                        <div className="w-full sm:w-auto">
                          <p className="text-sm sm:text-base">Speed Range:</p>
                          <input type="range" className="w-full" min="500" max="5000" value={5000 - speed + 500} onChange={handleSpeedChange} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full md:w-auto">
                    <EventFilterDropdown eventList={eventList} selectedEvents={selectedEvents} setSelectedEvents={setSelectedEvents} />
                  </div>
                </div>
              </Card>
            )}

            {/* MAP & DEVICE DETAILS (Always visible) */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
              <div className="col-span-3">
                <RouteMapView
                  mapInfo={mapInfo}
                  isPlaying={isPlaying}
                  speed={speed}
                  currentIndex={currentIndex}
                  setCurrentIndex={setCurrentIndex}
                  selectedEvents={selectedEvents}
                  overlayImg={overlayImg}
                  ignitionImg={ignitionImg}
                  haltImg={haltImg}
                  overSeedImg={overSeedImg}
                />
              </div>
              <DeviceDetails deviceDetails={deviceDetails} />
            </div>

            {/* CONDITIONAL CONTENT BASED ON TAB */}
            {activeTab === "static" ? (
              <>
                {/* Static Summary Data */}
                <div className="grid h-full grid-cols-1 gap-5 md:grid-cols-2">
                  <RouteSummary rowsPerPage={5} tempInfoName={tempInfoName} tempInfo={tempInfo} tempInfoDisplayNames={tempInfoDisplayNames} />
                  <KeyPlot rowsPerPage={5} utilInfoName={utilInfoName} utilInfo={utilInfo} utilInfoDisplayNames={utilInfoDisplayNames} mapInfo={mapInfo} />
                </div>
                <div>
                  <DeviceDetailsTable rowsPerPage={5} repDevInfoName={repDevInfoName} repDevInfo={repDevInfo} repDevInfoDisplayNames={repDevInfoDisplayNames} mapInfo={mapInfo} />
                </div>
                <RoutePieChart pieInfo={pieInfo} />
                <RouteDonutChart donutInfo={donutInfo} />
              </>
            ) : (
              <>
                {/* Playback Dynamic Data */}
                <RouteGauges gaugeInfo={gaugeInfo} latestValues={latestGaugeValues} mapInfo={mapInfo} isPlaying={isPlaying} currentIndex={currentIndex} />
                <div className="mt-2 mb-4">
                <StatusBanner currentData={mapInfo[currentIndex] || mapInfo[mapInfo.length - 1]} />
                </div>
                <RouteLineGraph lineGraphInfo={lineGraphInfo} currentIndex={currentIndex} isPlaying={isPlaying} />
                <div className="mt-4">
                   <RouteTile cardSliderInfo={cardSliderInfo} />
                </div>
              </>
            )}
            
          </div>
        </div>
      ) : (
        <div className="initialMessage flex flex-col items-center justify-center text-center">
          <img src={NoData} alt="noData" />
          <h4 className="mt-[-5rem] text-xl font-bold text-blue-1000 dark:text-white">Select a Route</h4>
          <p className="mb-6 font-dm text-sm font-medium text-gray-600">Please select Device ID and Date Range</p>
        </div>
      )}
    </>
  );
};

export default RouteTracking;